// ============================================
// Audio Cache Service with IndexedDB + LRU
// ============================================

import { AUDIO_CONFIG } from '@/constants';
import type { AudioCacheEntry } from '@/types';

// Cap the in-memory (session) blob cache so a long study run can't grow it
// without bound. Insertion-ordered: re-inserting refreshes recency, and the
// oldest key is evicted first once the cap is exceeded.
export const MEMORY_CACHE_MAX = 50;

/**
 * Insert into an insertion-ordered Map, evicting the oldest entries once size
 * exceeds `max`. Re-inserting an existing key refreshes its recency (moves it
 * to the newest position). Exported as a pure seam for unit testing.
 */
export function putWithCap<K, V>(map: Map<K, V>, key: K, value: V, max: number): void {
  if (map.has(key)) map.delete(key); // refresh recency
  map.set(key, value);
  while (map.size > max) {
    const oldest = map.keys().next().value as K;
    map.delete(oldest);
  }
}

// ============================================
// IndexedDB Wrapper
// ============================================

class AudioCacheDB {
  private db: IDBDatabase | null = null;
  private readonly storeName = 'audioCache';

  async open(): Promise<IDBDatabase> {
    if (this.db) return this.db;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(AUDIO_CONFIG.indexedDBName, AUDIO_CONFIG.indexedDBVersion);

      request.onerror = () => reject(request.error);

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'text' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  async get(text: string): Promise<AudioCacheEntry | undefined> {
    const db = await this.open();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.storeName, 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(text);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async set(entry: AudioCacheEntry): Promise<void> {
    const db = await this.open();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.put(entry);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async delete(text: string): Promise<void> {
    const db = await this.open();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(text);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getAll(): Promise<AudioCacheEntry[]> {
    const db = await this.open();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.storeName, 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Delete the oldest `n` entries by walking the `timestamp` index with a
  // key-only cursor (never materializes blob payloads), then deleting each
  // primary key. Returns the deleted keys so callers can prune mirrors.
  async deleteOldest(n: number): Promise<string[]> {
    if (n <= 0) return [];
    const db = await this.open();

    const keys = await new Promise<string[]>((resolve, reject) => {
      const transaction = db.transaction(this.storeName, 'readonly');
      const index = transaction.objectStore(this.storeName).index('timestamp');
      const collected: string[] = [];
      const request = index.openKeyCursor(); // ascending → oldest first

      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor && collected.length < n) {
          collected.push(cursor.primaryKey as string);
          cursor.continue();
        }
      };
      transaction.oncomplete = () => resolve(collected);
      transaction.onerror = () => reject(transaction.error);
    });

    await Promise.all(keys.map(key => this.delete(key)));
    return keys;
  }

  async count(): Promise<number> {
    const db = await this.open();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.storeName, 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.count();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async clear(): Promise<void> {
    const db = await this.open();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}

// ============================================
// Audio Cache Service
// ============================================

export class AudioCache {
  private db: AudioCacheDB;
  private memoryCache: Map<string, Blob> = new Map();

  constructor() {
    this.db = new AudioCacheDB();
  }

  // ==========================================
  // Core Cache Operations
  // ==========================================

  async get(text: string): Promise<Blob | null> {
    // Check memory cache first
    if (this.memoryCache.has(text)) {
      return this.memoryCache.get(text)!;
    }

    try {
      const entry = await this.db.get(text);

      if (entry) {
        // Check if expired
        if (Date.now() - entry.timestamp > AUDIO_CONFIG.cacheTTL) {
          await this.db.delete(text);
          return null;
        }

        // Add to memory cache for faster subsequent access
        putWithCap(this.memoryCache, text, entry.blob, MEMORY_CACHE_MAX);
        return entry.blob;
      }
    } catch (error) {
      console.warn('AudioCache get error:', error);
    }

    return null;
  }

  async set(text: string, blob: Blob): Promise<void> {
    try {
      // Add to memory cache (capped, insertion-ordered)
      putWithCap(this.memoryCache, text, blob, MEMORY_CACHE_MAX);

      // Check if we need to evict before adding
      await this.evictIfNeeded();

      // Add to IndexedDB
      await this.db.set({
        text,
        blob,
        timestamp: Date.now()
      });
    } catch (error) {
      console.warn('AudioCache set error:', error);
    }
  }

  async has(text: string): Promise<boolean> {
    if (this.memoryCache.has(text)) return true;

    try {
      const entry = await this.db.get(text);
      return !!entry && (Date.now() - entry.timestamp <= AUDIO_CONFIG.cacheTTL);
    } catch {
      return false;
    }
  }

  // ==========================================
  // LRU Eviction
  // ==========================================

  private async evictIfNeeded(): Promise<void> {
    try {
      const count = await this.db.count();

      if (count >= AUDIO_CONFIG.maxCacheEntries) {
        // Delete the oldest ~10% via a key-only cursor on the timestamp
        // index — no blob payloads are read into memory to do this.
        const toRemove = Math.ceil(AUDIO_CONFIG.maxCacheEntries * 0.1);
        const removed = await this.db.deleteOldest(toRemove);
        for (const key of removed) {
          this.memoryCache.delete(key);
        }
      }
    } catch (error) {
      console.warn('AudioCache eviction error:', error);
    }
  }

  // ==========================================
  // Maintenance
  // ==========================================

  async cleanup(): Promise<void> {
    try {
      const entries = await this.db.getAll();
      const now = Date.now();

      for (const entry of entries) {
        if (now - entry.timestamp > AUDIO_CONFIG.cacheTTL) {
          await this.db.delete(entry.text);
          this.memoryCache.delete(entry.text);
        }
      }
    } catch (error) {
      console.warn('AudioCache cleanup error:', error);
    }
  }

  async clear(): Promise<void> {
    this.memoryCache.clear();
    await this.db.clear();
  }

  async getStats(): Promise<{ count: number; memoryCount: number }> {
    const count = await this.db.count();
    return {
      count,
      memoryCount: this.memoryCache.size
    };
  }
}

// ============================================
// Singleton Export
// ============================================

export const audioCache = new AudioCache();
